import { useEffect, useReducer } from 'react'
import { client } from '../../graphql/client'
import { GET_SHOP_QUERY } from '../../graphql/query'
import { Shop, VariantOption, VariantType } from '../type/backendType'
import { IModel, IContextState } from '../type/modelType'

type Action =
  | { type: 'setModel'; payload: IModel }
  | { type: 'setShop'; payload: Shop }
  | { type: 'setSalesChannelId'; payload: string }
  | { type: 'setReady'; payload: boolean }
  | { type: 'setColorsLibrary'; payload: VariantOption[] }
  | { type: 'setTexturesLibrary'; payload: VariantOption[] }

export const initialState: IContextState = {
  ready: false,
  colorsLibrary: [],
  texturesLibrary: [],
  setModel: (model: IModel) => {},
  setShop: (shop: Shop) => {},
  setSalesChannelId: (salesChannelId: string) => {},
  fetchShop: () => {},
  setReady: () => {},
}

function reducer(state: IContextState, action: Action) {
  const currentState = state
  switch (action.type) {
    case 'setModel':
      const model = action.payload
      return { ...currentState, model: { current: model } }
    case 'setShop':
      const shop = action.payload
      return { ...currentState, shop }
    case 'setSalesChannelId':
      const salesChannelId = action.payload
      return { ...currentState, salesChannelId }
    case 'setReady':
      const ready = action.payload
      return { ...currentState, ready }
    case 'setColorsLibrary':
      const colorsLibrary = action.payload
      return { ...currentState, colorsLibrary }
    case 'setTexturesLibrary':
      const texturesLibrary = action.payload
      return { ...currentState, texturesLibrary }
    default:
      return currentState
  }
}

export const useStudio = () => {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    fetchShop()
    setSalesChannelId(process.env.REACT_APP_SALESCHANNEL_ID as string)
  }, [])

  const fetchShop = () => {
    client
      .query({
        query: GET_SHOP_QUERY,
        variables: { shopId: process.env.REACT_APP_SHOP_ID },
      })
      .then(result => {
        if (result.data) {
          const shop = result.data.shop as Shop

          const allColors = shop.variants.edges.filter(
            variant =>
              variant.node.type === VariantType.COLOR &&
              variant.node.no === '3d-colors-library'
          )?.[0]?.node.options

          const allTextures = shop.variants.edges.filter(
            variant =>
              variant.node.type === VariantType.COLOR &&
              variant.node.no === '3d-textures-library'
          )?.[0]?.node.options

          setColorsLibrary(allColors ?? [])
          setTexturesLibrary(allTextures ?? [])
          setShop(shop)
        }
      })
  }

  const setModel = (model: IModel) => {
    dispatch({ type: 'setModel', payload: model })
  }

  const setShop = (shop: Shop) => {
    dispatch({ type: 'setShop', payload: shop })
  }

  const setSalesChannelId = (salesChannelId: string) => {
    dispatch({ type: 'setSalesChannelId', payload: salesChannelId })
  }

  const setReady = (ready: boolean) => {
    dispatch({ type: 'setReady', payload: ready })
  }

  const setColorsLibrary = (colors: VariantOption[]) => {
    dispatch({ type: 'setColorsLibrary', payload: colors })
  }

  const setTexturesLibrary = (textures: VariantOption[]) => {
    dispatch({ type: 'setTexturesLibrary', payload: textures })
  }

  return {
    ...state,
    setModel,
    setShop,
    setSalesChannelId,
    fetchShop,
    setReady,
  }
}
